import React, { useState } from 'react';
import { fmt, bandColor, CRIT_COLOR, CRIT_ORDER, CRIT_ABBR } from '../../lib/format.js';

/**
 * RadarChart — compares 2+ series across N axes.
 * axes: [{key, label}], series: [{name, values:{key: 0..max}, color}]
 */
export function RadarChart({ axes, series, max = 5, size = 320 }) {
  const cx = size / 2, cy = size / 2;
  const R = size / 2 - 44;
  const nAxes = axes.length;
  const angle = (i) => -Math.PI / 2 + (i / nAxes) * 2 * Math.PI;
  const point = (i, v) => {
    const r = (Math.max(0, Math.min(max, v)) / max) * R;
    return [cx + r * Math.cos(angle(i)), cy + r * Math.sin(angle(i))];
  };
  // Concentric grid rings
  const rings = [0.25, 0.5, 0.75, 1];

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} style={{ maxWidth: '100%', height: 'auto' }} role="img" aria-label="Radar comparison">
      {/* Grid */}
      {rings.map((f, i) => (
        <polygon key={i}
          points={axes.map((_, j) => {
            const r = f * R;
            return `${cx + r * Math.cos(angle(j))},${cy + r * Math.sin(angle(j))}`;
          }).join(' ')}
          fill="none" stroke="var(--line-2)" strokeWidth={i === rings.length - 1 ? 1.4 : 0.8}
        />
      ))}
      {/* Spokes */}
      {axes.map((_, i) => {
        const [x, y] = point(i, max);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="var(--line-2)" strokeWidth="0.8" />;
      })}
      {/* Series */}
      {series.map((s, si) => {
        const pts = axes.map((a, i) => point(i, s.values[a.key] || 0));
        const d = pts.map((p, i) => (i ? 'L' : 'M') + ' ' + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ') + ' Z';
        return (
          <g key={s.name}>
            <path d={d} fill={s.color} fillOpacity={0.15} stroke={s.color} strokeWidth="2" strokeLinejoin="round" />
            {pts.map((p, i) => (
              <circle key={i} cx={p[0]} cy={p[1]} r="3.5" fill={s.color} stroke="#fff" strokeWidth="1.2" />
            ))}
          </g>
        );
      })}
      {/* Axis labels */}
      {axes.map((a, i) => {
        const r = R + 22;
        const x = cx + r * Math.cos(angle(i));
        const y = cy + r * Math.sin(angle(i));
        return (
          <text key={a.key} x={x} y={y}
            textAnchor="middle" dominantBaseline="middle"
            fontSize="11" fontWeight="700" fill="var(--ink-2)"
          >
            {a.label}
          </text>
        );
      })}
      {/* Ring value labels (right axis only) */}
      {rings.map((f, i) => (
        <text key={i} x={cx + 4} y={cy - f * R + 3}
          fontSize="9" fill="var(--ink-4)" className="tabular" fontWeight="600">
          {fmt(f * max, 1)}
        </text>
      ))}
    </svg>
  );
}


/**
 * ScoreDistribution — histogram of project scores across bands.
 * projects: [{overall}], bins fixed at 0-2.5 / 2.6-3.5 / 3.6-4.5 / 4.6-5
 */
export function ScoreDistribution({ projects, testid }) {
  const BANDS = [
    { label: 'Poor', min: 0, max: 2.6, color: '#c8452f' },
    { label: 'Moderate', min: 2.6, max: 3.6, color: '#d98a1f' },
    { label: 'Good', min: 3.6, max: 4.6, color: '#5aa06a' },
    { label: 'Excellent', min: 4.6, max: 5.01, color: '#1f8a4c' },
  ];
  // Finer 20 bins for the histogram, keep colours per band
  const NBINS = 20;
  const step = 5 / NBINS;
  const bins = Array.from({ length: NBINS }, (_, i) => ({
    from: i * step, to: (i + 1) * step, count: 0, color: null,
  }));
  projects.filter((p) => p.overall !== null && p.overall !== undefined).forEach((p) => {
    const idx = Math.min(NBINS - 1, Math.floor(p.overall / step));
    bins[idx].count += 1;
  });
  // colour each bin by band midpoint
  bins.forEach((b) => {
    const mid = (b.from + b.to) / 2;
    const band = BANDS.find((x) => mid >= x.min && mid < x.max);
    b.color = band ? band.color : '#98a1b8';
    b.band = band ? band.label : null;
  });

  const W = 640, H = 220, P = { l: 30, r: 12, t: 20, b: 34 };
  const maxCount = Math.max(1, ...bins.map((b) => b.count));
  const bw = (W - P.l - P.r) / NBINS;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Project score distribution" data-testid={testid}>
      {/* Gridlines */}
      {[0.25, 0.5, 0.75, 1].map((t) => (
        <line key={t}
          x1={P.l} x2={W - P.r}
          y1={P.t + t * (H - P.t - P.b)} y2={P.t + t * (H - P.t - P.b)}
          stroke="var(--line-2)" strokeDasharray="3 3"
        />
      ))}
      {/* Bars */}
      {bins.map((b, i) => {
        const h = (b.count / maxCount) * (H - P.t - P.b);
        const x = P.l + i * bw;
        const y = H - P.b - h;
        return (
          <g key={i}>
            <rect x={x + 0.7} y={y} width={bw - 1.4} height={h} rx="2" fill={b.color} fillOpacity={0.88}>
              <title>{`${fmt(b.from, 1)}–${fmt(b.to, 1)} · ${b.count} project${b.count === 1 ? '' : 's'} · ${b.band}`}</title>
            </rect>
            {b.count > 0 && (
              <text x={x + bw / 2} y={y - 3} textAnchor="middle" fontSize="9" fontWeight="700" fill="var(--ink-2)" className="tabular">
                {b.count}
              </text>
            )}
          </g>
        );
      })}
      {/* Band separators */}
      {BANDS.slice(1).map((b) => {
        const x = P.l + (b.min / 5) * (W - P.l - P.r);
        return <line key={b.label} x1={x} x2={x} y1={P.t} y2={H - P.b} stroke="var(--ink-4)" strokeDasharray="4 3" strokeWidth="1" opacity="0.4" />;
      })}
      {/* X-axis ticks */}
      {[0, 1, 2, 3, 4, 5].map((t) => (
        <text key={t} x={P.l + (t / 5) * (W - P.l - P.r)} y={H - P.b + 14}
          textAnchor="middle" fontSize="10" fill="var(--ink-4)" className="tabular">
          {t.toFixed(1)}
        </text>
      ))}
      <text x={W / 2} y={H - 4} textAnchor="middle" fontSize="10.5" fill="var(--ink-3)" fontWeight="600">
        OECD overall score
      </text>
    </svg>
  );
}


/**
 * BubblePlot — x=budget, y=score, r=projects, colour by band.
 * points: [{name, x, y, r, band, [sub]}]
 */
export function BubblePlot({ points, xLabel = 'Budget (₹ Cr)', yLabel = 'Overall score', testid }) {
  const [hover, setHover] = useState(null);
  const W = 720, H = 380, P = { l: 46, r: 20, t: 16, b: 36 };
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const rs = points.map((p) => p.r);
  const xMax = Math.max(1, ...xs) * 1.08;
  const yMax = 5;
  const rMax = Math.max(1, ...rs);
  const sx = (v) => P.l + (v / xMax) * (W - P.l - P.r);
  const sy = (v) => H - P.b - (v / yMax) * (H - P.t - P.b);
  const sr = (v) => 6 + Math.sqrt(v / rMax) * 16;

  return (
    <div style={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Budget vs score bubble plot" data-testid={testid}>
        {/* Y grid */}
        {[1, 2, 3, 4, 5].map((y) => (
          <g key={y}>
            <line x1={P.l} x2={W - P.r} y1={sy(y)} y2={sy(y)} stroke="var(--line-2)" strokeDasharray="3 3" />
            <text x={P.l - 6} y={sy(y) + 3} textAnchor="end" fontSize="10" fill="var(--ink-4)" className="tabular">{y}</text>
          </g>
        ))}
        {/* X ticks */}
        {[0, 0.25, 0.5, 0.75, 1].map((t) => (
          <text key={t} x={sx(t * xMax)} y={H - P.b + 16} textAnchor="middle" fontSize="10" fill="var(--ink-4)" className="tabular">
            {(t * xMax).toFixed(1)}
          </text>
        ))}
        {/* Axis labels */}
        <text x={W / 2} y={H - 6} textAnchor="middle" fontSize="10.5" fontWeight="600" fill="var(--ink-3)">{xLabel}</text>
        <text x={12} y={H / 2} transform={`rotate(-90 12 ${H / 2})`} textAnchor="middle" fontSize="10.5" fontWeight="600" fill="var(--ink-3)">{yLabel}</text>
        {/* Points */}
        {points.map((p, i) => (
          <circle key={i}
            cx={sx(p.x)} cy={sy(p.y)} r={sr(p.r)}
            fill={bandColor(p.band)} fillOpacity="0.5"
            stroke={bandColor(p.band)} strokeWidth="1.5"
            style={{ cursor: 'pointer', transition: 'fill-opacity .15s' }}
            onMouseEnter={() => setHover({ ...p, ix: i, cx: sx(p.x), cy: sy(p.y) })}
            onMouseLeave={() => setHover(null)}
          >
            <title>{`${p.name} · ₹${p.x.toFixed(1)} Cr · ${p.y.toFixed(2)} · ${p.r} projects`}</title>
          </circle>
        ))}
        {hover && (
          <g pointerEvents="none">
            <circle cx={hover.cx} cy={hover.cy} r={sr(hover.r) + 3} fill="none" stroke="var(--ink)" strokeWidth="2" />
          </g>
        )}
      </svg>
      {hover && (
        <div style={{
          position: 'absolute',
          left: `${(hover.cx / W) * 100}%`,
          top: `${(hover.cy / H) * 100}%`,
          transform: 'translate(14px, -100%)',
          background: '#042a54', color: '#fff', padding: '8px 12px', borderRadius: 8,
          fontSize: 12, minWidth: 180, pointerEvents: 'none', boxShadow: 'var(--shadow-lg)',
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{hover.name}</div>
          <div>Budget · <b>₹{hover.x.toFixed(1)} Cr</b></div>
          <div>Score · <b className="tabular">{hover.y.toFixed(2)}</b> ({hover.band})</div>
          <div>Projects · <b>{hover.r}</b></div>
          {hover.sub && <div style={{ opacity: .7, marginTop: 3, fontSize: 11 }}>{hover.sub}</div>}
        </div>
      )}
    </div>
  );
}
